// AdminSidebar — the admin frame's left section nav (desktop only; on phones the
// admin sections live in the hamburger drawer, so this rail is hidden <md to
// avoid competing with it). Renders the `admin` group of the ONE nav-config
// registry, grouped into sections (People / Project / Contract / System) via
// visibleAdminSections — same source as the gear + the hamburger, so they can't
// disagree.
//
// Collapsible (grouped+labelled ⇄ icon rail), state in the persisted sidebar
// store. Collapsed links carry an aria-label so the icon-only rail keeps an
// accessible name. Sticky under the top bar (which stays sticky on admin pages —
// the rail is the in-context nav, so the global bar above it is kept, not hidden).

import { useTranslation } from 'react-i18next';
import { icons } from '../ui/icons.js';
import { NavLink } from '../ui/NavLink.js';
import { IconButton } from '../ui/IconButton.js';
import { Tooltip } from '../ui/Tooltip.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useSidebarStore } from '../../stores/sidebarStore.js';
import { visibleAdminSections, navItemPath } from '../../config/nav.js';

export function AdminSidebar({ projectId }: { projectId: string }) {
  const { t } = useTranslation('nav');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const sections = visibleAdminSections({ can, entitlements });
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  // A row whose route PREFIXES a sibling's must match exactly (NavLink
  // prefix-matches by default): 'settings' beside 'settings/files' would
  // otherwise both light up as the current page.
  const allRoutes = sections.flatMap((s) => s.items).map((i) => i.route);
  const exactFor = (route: string) => allRoutes.some((r) => r !== route && r.startsWith(`${route}/`));

  return (
    <nav
      aria-label={t('landmarks.admin')}
      className={`hidden md:flex shrink-0 border-e border-[var(--color-border)] bg-[var(--color-chrome)] flex-col
        sticky top-[var(--shell-top)] self-start h-[calc(100dvh_-_var(--shell-top))] transition-[width] duration-base
        ${collapsed ? 'w-16' : 'w-60'}`}
    >
      <div
        className={`flex items-center h-12 shrink-0 border-b border-[var(--color-border)] ${
          collapsed ? 'justify-center' : 'justify-between px-3'
        }`}
      >
        {!collapsed && (
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
            {t('admin.title')}
          </span>
        )}
        <IconButton
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-expanded={!collapsed}
          onClick={toggleCollapsed}
        >
          {/* Directional carets mirror under RTL (DESIGN-TOKENS §Responsive #6). */}
          {collapsed ? (
            <icons.caretRight aria-hidden size={16} className="rtl:-scale-x-100" />
          ) : (
            <icons.caretLeft aria-hidden size={16} className="rtl:-scale-x-100" />
          )}
        </IconButton>
      </div>

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5">
        {collapsed
          ? // Icon rail — flat, no section headings. aria-label names each for SR;
            // a right-placed Tooltip reveals the name to sighted users on hover +
            // keyboard focus (icons alone are ambiguous across many admin items).
            sections.flatMap((s) => s.items).map((item) => {
              const Icon = item.icon;
              return (
                <Tooltip key={item.id} content={t(item.labelKey)} placement="end">
                  <NavLink
                    to={item.external ? item.route : navItemPath(projectId, item.route)}
                    external={item.external}
                    variant="sidebar"
                    aria-label={t(item.labelKey)}
                    className="justify-center px-0"
                    {...(item.external ? {} : { end: exactFor(item.route) })}
                  >
                    <Icon aria-hidden size={18} className="shrink-0" />
                  </NavLink>
                </Tooltip>
              );
            })
          : // Grouped — a section label per group, then its items.
            sections.map((section) => (
              <div key={section.section} role="group" aria-label={t(section.labelKey)}>
                <div
                  aria-hidden
                  className="px-3 pt-3 pb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--color-text-disabled)]"
                >
                  {t(section.labelKey)}
                </div>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.external ? item.route : navItemPath(projectId, item.route)}
                      external={item.external}
                      variant="sidebar"
                      {...(item.external ? {} : { end: exactFor(item.route) })}
                    >
                      <Icon aria-hidden size={18} className="shrink-0" />
                      <span className="truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  );
                })}
              </div>
            ))}
      </div>
    </nav>
  );
}
